import { Icon } from '@civilgrid/ui/components';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import FeatureFlags, { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import type { Project } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import { useConflictsSummaryPath } from '~/hooks/useConflictsSummaryPath';
import { useIntelligenceTabRedesignEnabled } from '~/hooks/useIntelligenceTabRedesignEnabled';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';

export default function IntelligenceV2Header({
  fallback,
  headingId,
}: {
  project: Project;
  fallback?: React.ReactNode;
  headingId?: string;
}) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();
  const { enabled: intelligenceV2Enabled } = useIntelligenceTabRedesignEnabled();
  const conflictSummaryPath = useConflictsSummaryPath();

  useSnapshot(rootStore);
  const hasConflictsSummary = FeatureFlags.hasOrgFeatureFlag(OrgFeatureFlag.ConflictsSummary);

  if (!intelligenceV2Enabled) {
    return fallback;
  }

  const title = (
    <h2 id={headingId} className="text-lg font-medium">
      Intelligence
    </h2>
  );

  const conflictsSummaryLink = (
    <Link
      to={conflictSummaryPath}
      className="-mx-1 -my-1 flex items-center gap-1 rounded-sm px-1 py-1 text-xs font-medium text-blue-50 focus-ring"
      data-testid="intelligence-v2-header-back-link"
    >
      <Icon icon="arrowLeft" size={14} />
      Conflicts Summary
    </Link>
  );

  return (
    <div className="mb-3 flex flex-col gap-3" data-testid="intelligence-v2-header">
      <div className="flex items-center justify-between">
        {hasConflictsSummary ? conflictsSummaryLink : title}

        <CloseIconButton
          onClick={() => navigate({ to: '/projects/$projectId', params: { projectId } })}
          aria-label="Close intelligence"
          data-testid="project-detail-subtab-close"
        />
      </div>
      {hasConflictsSummary && title}
    </div>
  );
}
